/**
 * MUI RadioButtons Component
 * 
 * This component provides radio button functionality for single-selection scenarios.
 * Used in navigation sections for view selection (Summary, Positioning details, etc.)
 */

import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  styled
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { getAreaState } from 'reduxModules/selectors/areaSelectors';
import ReducersIndex from 'reduxModules/ducks';
import { uiReducers } from 'reduxModules/ducks/ui';
import { isDevioUcxEligible } from 'toolConstants/conferenceroomDesigner/treeFrogPorts';
import { MIC_CATEGORIES } from 'types/models/microphone';
import { RadioButtonsProps } from 'types/components/molecules';
import type { RootState } from 'types/redux/state';

/**
 * RadioButtons component for single selection with vertical layout
 * 
 * @param props - Component props
 * @returns RadioButtons component
 */
const RadioButtons: React.FC<RadioButtonsProps> = (props) => {
  const {
    label,
    name,
    items = [],
    dispatcher,
    className = '',
    isNav = false,
    isPlatform = false,
    ...otherProps
  } = props;

  const dispatch = useDispatch();
  
  // For showTable, read from UI state; for everything else, use getCurrentValue2
  const currentValue = useSelector((state: RootState) => 
    name === 'showTable' ? state.ui.showTable : getCurrentValue2(state, name)
  );

  // Handle default selection for equipment list navigation
  const isEquipmentListNav = name === 'showTable' && isNav && dispatcher === 'setShowTable';
  // Platform (UC Compute → Platform → UC device) defaults to Windows until the
  // user explicitly picks Android.
  const isPlatformSelector = isPlatform && name === 'platform' && dispatcher === 'setPlatform';

  // Bar mic + expansion mics/speakers makes Devio UCX (Android) the only valid
  // platform (see isDevioUcxEligible's `exclusive` flag). The reducers already
  // force platform=Android and reject a manual switch to Windows in that case —
  // this just reflects that lock in the UI: the control stays visible (shows
  // Android selected) but disabled, instead of being hidden.
  const area = useSelector((state: RootState) => (isPlatformSelector ? getAreaState(state) : undefined));
  const androidLocked = isPlatformSelector && Boolean(area) && isDevioUcxEligible(area).exclusive;
  // BMA360D (ceiling-tile mic) is Dante-based and never rides Devio UCX — pin
  // Platform to Windows and disable the control, the mirror image of the
  // Android lock above (see the matching "BMA360D" branch in
  // revalidateUcxEligibility, reduxModules/ducks/room.ts).
  const windowsLocked = isPlatformSelector && Boolean(area) &&
    MIC_CATEGORIES.CEILING_TILE.has((area as any)?.microphones?.model);
  const platformLocked = androidLocked || windowsLocked;

  const defaultValue = isEquipmentListNav
    ? 'summary'
    : (isPlatformSelector ? (androidLocked ? 'Android' : 'Windows') : '');
  const effectiveValue = currentValue || defaultValue;

  // Set default value for equipment list navigation if no current value exists
  React.useEffect(() => {
    if (isEquipmentListNav && !currentValue) {
      dispatch(uiReducers.setShowTable('summary'));
    }
    if (isPlatformSelector && !currentValue) {
      const containerActionCreator = (ReducersIndex.container as any)['setPlatform'];
      if (containerActionCreator) {
        dispatch(containerActionCreator(androidLocked ? 'Android' : 'Windows'));
      }
    }
  }, [isEquipmentListNav, isPlatformSelector, androidLocked, currentValue, dispatch]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (dispatcher === 'setShowTable') {
      dispatch(uiReducers.setShowTable(value));
      return;
    }
    if (!dispatcher) return;
    // Call the action creator directly so the action type stays in sync with the
    // slice if it's ever renamed. The `ContainerActionCreators` cast in
    // reduxModules/ducks/index.ts already types the payload permissively.
    const containerActionCreator = (ReducersIndex.container as any)[dispatcher];
    if (containerActionCreator) {
      // Platform only: mark this as a genuine direct pick (spec §2.1's
      // androidExplicitlySelected) via meta, distinct from the mount-time
      // default-value effect below, which dispatches the same action with no
      // meta when a bar + expansion design auto-forces Android. Harmless for
      // every other dispatcher — createSlice's default action creators
      // ignore a second argument.
      if (isPlatformSelector) {
        dispatch(containerActionCreator(value, { userInitiated: true }));
        return;
      }
      dispatch(containerActionCreator(value));
      return;
    }
    // Fallback for dispatchers that don't live on the container slice.
    dispatch({ type: dispatcher, payload: value });
  };

  return (
    <StyledBox className={className} isNav={isNav}>
      <FormControl component="fieldset" fullWidth>
        {label && (
          <StyledFormLabel isNav={isNav} isPlatform={isPlatform}>
            {label}
          </StyledFormLabel>
        )}
        <RadioGroup
          name={name}
          onChange={handleChange}
          row={isPlatform}
          {...otherProps}
          // Ensure value prop is not overridden by otherProps
          value={effectiveValue}
        >
          {items.map((item, index) => {
            const value = typeof item === 'object' ? item.value : item;
            const displayLabel = typeof item === 'object' ? item.label : item;

            return (
              <StyledFormControlLabel
                key={`${name}-${index}`}
                value={value}
                control={<StyledRadio isNav={isNav} isPlatform={isPlatform} disabled={platformLocked} />}
                label={displayLabel}
                isNav={isNav}
                isPlatform={isPlatform}
                disabled={platformLocked}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </StyledBox>
  );
};

// Styled components for consistent theming
const StyledBox = styled(Box)<{ isNav?: boolean }>(({ theme, isNav }) => ({
  width: '100%',
  ...(isNav && {
    '& .MuiFormGroup-root': {
      gap: '4px',
    }
  })
}));

const StyledFormLabel = styled(FormLabel)<{ isNav?: boolean; isPlatform?: boolean }>(({ theme, isNav, isPlatform }) => ({
  '&.MuiFormLabel-root': {
    color: isPlatform ? theme.palette.text.primary : (isNav ? '#b0b0b0' : theme.palette.text.primary),
  },
  // Platform reuses the same label styling as the atoms/InputLabel component
  // (used by the Product Family selector directly below it in the equipment
  // list) so the two labels read consistently.
  ...(isPlatform
    ? {
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 'normal',
        letterSpacing: '-0.24px',
        marginBottom: '5px',
        fontFamily: '"Open Sans"',
      }
    : {
        fontSize: isNav ? '12px' : '14px',
        fontWeight: 'normal',
        marginBottom: isNav ? '8px' : '4px',
      }),
  '&.MuiFormLabel-root.Mui-focused': {
    color: isPlatform ? theme.palette.text.primary : (isNav ? '#b0b0b0' : theme.palette.primary.main),
  }
}));

const StyledFormControlLabel = styled(FormControlLabel)<{ isNav?: boolean; isPlatform?: boolean }>(({ theme, isNav, isPlatform }) => ({
  margin: '0',
  marginLeft: isNav ? '8px' : '0',
  '& .MuiFormControlLabel-label': {
    color: isPlatform ? theme.palette.text.primary : (isNav ? '#b0b0b0' : theme.palette.text.primary),
    fontSize: isNav ? '14px' : '16px',
    lineHeight: '1.2',
  }
}));

const StyledRadio = styled(Radio)<{ isNav?: boolean; isPlatform?: boolean }>(({ theme, isNav, isPlatform }) => ({
  color: isPlatform ? theme.palette.text.primary : (isNav ? '#b0b0b0' : theme.palette.action.active),
  padding: isNav ? '4px 8px' : '9px',
  '&.Mui-checked': {
    color: isPlatform ? theme.palette.primary.main : (isNav ? '#007bff' : theme.palette.primary.main),
  },
  '& .MuiSvgIcon-root': {
    fontSize: isNav ? '16px' : '20px',
  }
}));

export type { RadioButtonsProps };
export default RadioButtons;
